import React, { useState } from "react";
import { useConfirm } from "./ConfirmModal";
import Icons from "../icons";
import HelpButton from "./HelpDialog";
import { DIALOG_HEIGHT } from "./dialogSize";

/** Small summary of pass/fail counts for a board. */
function summarize(board) {
  // API boards carry aggregated counts; fall back to a testCases array if present.
  if (board && typeof board.total === "number") {
    return {
      total: board.total,
      passed: board.passed || 0,
      failed: board.failed || 0,
    };
  }
  const testCases = board?.testCases || [];
  const total = testCases.length;
  const passed = testCases.filter((t) => t.status === "passed").length;
  const failed = testCases.filter((t) => t.status === "failed").length;
  return { total, passed, failed };
}

/* ------------------------------------------------------------------ */
/*  Card view                                                          */
/* ------------------------------------------------------------------ */
function BoardCard({ board, onOpen, onDelete }) {
  const { total, passed, failed } = summarize(board);
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => onOpen(board.id)}
        className="flex w-full flex-col rounded-xl border border-slate-200 bg-card p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
      >
        <div className="flex items-start justify-between">
          <div className={`flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br ${board.accent} text-white`}>
            <Icons.Grid className="h-5 w-5" />
          </div>
          <Icons.ChevronRight className="h-5 w-5 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
        </div>

        <h3 className="mt-4 font-semibold text-slate-900">{board.name}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-slate-500">{board.description}</p>

        <div className="mt-4 flex items-center gap-4 text-xs">
          <span className="font-medium text-slate-500">{total} tests</span>
          <span className="flex items-center gap-1 text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {passed} passed
          </span>
          {failed > 0 && (
            <span className="flex items-center gap-1 text-rose-600">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
              {failed} failed
            </span>
          )}
        </div>
      </button>

      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(board);
          }}
          aria-label="Delete test suite"
          title="Delete test suite"
          className="absolute bottom-4 right-4 rounded-md p-1.5 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 focus:opacity-100 group-hover:opacity-100"
        >
          <Icons.Trash className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Grid (table) view                                                  */
/* ------------------------------------------------------------------ */
function BoardsTable({ boards, onOpen, onDelete }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <thead>
            <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Test Suite</th>
              <th className="px-4 py-3">Tests</th>
              <th className="px-4 py-3">Passed</th>
              <th className="px-4 py-3">Failed</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {boards.map((board) => {
              const { total, passed, failed } = summarize(board);
              return (
                <tr
                  key={board.id}
                  onClick={() => onOpen(board.id)}
                  className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${board.accent} text-white`}>
                        <Icons.Grid className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{board.name}</div>
                        <div className="truncate text-xs text-slate-500">{board.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-600">{total}</td>
                  <td className="px-4 py-3 text-sm text-emerald-600">{passed}</td>
                  <td className="px-4 py-3 text-sm text-rose-600">{failed || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-700">
                        Open <Icons.ChevronRight className="h-4 w-4" />
                      </span>
                      {onDelete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(board);
                          }}
                          aria-label="Delete test suite"
                          title="Delete test suite"
                          className="rounded-md p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-500"
                        >
                          <Icons.Trash className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Add board modal                                                    */
/* ------------------------------------------------------------------ */
function AddBoardModal({ onClose, onCreate }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const canCreate = name.trim().length > 0;

  const inputCls =
    "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

  const submit = () => {
    if (!canCreate) return;
    onCreate({ name: name.trim(), description: description.trim() });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex w-full max-w-md flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">Add test suite</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Test Suite name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Checkout Regression Suite"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Description <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What does this suite cover?"
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex flex-none justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canCreate}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Create test suite
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  View toggle (card / grid)                                          */
/* ------------------------------------------------------------------ */
function ViewToggle({ view, onChange }) {
  const options = [
    { id: "card", label: "Card view", icon: Icons.Grid },
    { id: "grid", label: "Grid view", icon: Icons.List },
  ];
  return (
    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-card p-0.5">
      {options.map((opt) => {
        const OptIcon = opt.icon;
        const isActive = view === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            title={opt.label}
            aria-label={opt.label}
            aria-pressed={isActive}
            className={[
              "inline-flex h-8 w-8 items-center justify-center rounded-md transition",
              isActive ? "bg-brand-600 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
            ].join(" ")}
          >
            <OptIcon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

export default function BoardsView({ boards, onOpenBoard, onAddBoard, onDeleteBoard }) {
  const [showAdd, setShowAdd] = useState(false);
  const [view, setView] = useState("card");
  const [query, setQuery] = useState("");

  const [ask, confirmDialog] = useConfirm();

  const confirmDelete = async (board) => {
    const ok = await ask({
      title: "Delete test suite",
      message: `Delete test suite "${board.name}" and all its test cases, categories and run history? This cannot be undone.`,
    });
    if (ok) onDeleteBoard?.(board.id);
  };

  const q = query.trim().toLowerCase();
  const filteredBoards = q
    ? boards.filter(
        (board) =>
          board.name.toLowerCase().includes(q) ||
          (board.description || "").toLowerCase().includes(q)
      )
    : boards;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Test Suites</h1>
          <p className="mt-1 text-sm text-slate-500">
            Select a test suite to view and run its test cases.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle view={view} onChange={setView} />
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800"
          >
            <Icons.Plus className="h-4 w-4" />
            Add test suite
          </button>
          <HelpButton screen="boards" />
        </div>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search test suites by name or description..."
            aria-label="Search test suites"
            className="w-full rounded-lg border border-slate-300 bg-card py-2 pl-9 pr-9 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {filteredBoards.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-12 text-center">
          <p className="text-sm text-slate-500">
            No boards match <span className="font-medium text-slate-700">"{query}"</span>.
          </p>
        </div>
      ) : view === "card" ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {filteredBoards.map((board) => (
            <BoardCard key={board.id} board={board} onOpen={onOpenBoard} onDelete={confirmDelete} />
          ))}
        </div>
      ) : (
        <BoardsTable boards={filteredBoards} onOpen={onOpenBoard} onDelete={confirmDelete} />
      )}

      {showAdd && (
        <AddBoardModal onClose={() => setShowAdd(false)} onCreate={onAddBoard} />
      )}
      {/* The questions handlers ask before they act (useConfirm). */}
      {confirmDialog}
    </div>
  );
}
